import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, ChevronRight, FileText, HardHat, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ContractorSuggestionPanel } from "@/features/contractors/components/ContractorSuggestionPanel";
import { sortLeaveLast, withLeaveSuffix } from "@/features/contractors/api/leaves";
import { RequisitionApprovalNotice } from "@/features/orders/components/RequisitionApprovalNotice";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { LineItemFormRow, lineItemSchema } from "@/features/orders/components/LineItemFormRow";
import { AutoLineItemRow, autoRowQuantity, autoTriggerLabel } from "@/features/orders/components/AutoLineItemRow";
import { isWpwPackageUsable } from "../api";
import { useActiveContractors, useAutoLineItemsPreview, useContractorAgreements, useCreateWpwRequisition, useWpwPackages } from "../hooks";
import type { WpwPackageRow } from "../api";
import type { Parcel, ParcelOwner } from "../types";
import { groupOwnersForDisplay } from "../ownerDisplay";
import { isWpwCandidateParcel } from "../utils";
import { MultiFileInput } from "./ui";
import { useAuthStore } from "@/store/auth";

const schema = z.object({
  contractor: z.string().min(1, "Wybierz podwykonawcę do zlecenia WPW"),
  description: z.string().optional(),
  deadline: z.string().optional(),
  wpw_order_type: z.enum(["wpw_kompleksowe", "wpw_terenowe"]),
  line_items: z.array(lineItemSchema).min(1, "Dodaj co najmniej jedną pozycję realizacyjną"),
});
type FormData = z.infer<typeof schema>;

// One WPW = one PRIVATE owner. The ONLY owner we block from a fresh negotiator is one whose consent
// is already SECURED on the current route ("już pozyskany"). An owner who already has a WPW order in
// progress ("w trakcie / w terenie") is SHOWN with a badge but stays selectable — the user may
// deliberately send another order, so we don't block it (just don't pre-select it).
type OwnerRow = { owner: ParcelOwner; secured: boolean; inField: boolean; blocked: boolean };
type ParcelGroup = { parcel: Parcel; rows: OwnerRow[] };

function buildGroups(parcels: Parcel[]): ParcelGroup[] {
  const groups: ParcelGroup[] = [];
  for (const p of parcels) {
    // Only parcels under the CURRENT route (+ manually added ones), actually USED by the
    // project (a "na zapas" parcel the route merely passes by has nothing to negotiate) and
    // on the consent path — one shared rule with the WPW tab and the backend's
    // `_private_parcels`, so the picker can't offer work the sequence won't do.
    if (!isWpwCandidateParcel(p)) continue;
    const rows: OwnerRow[] = p.owners
      .filter((o) => !o.office) // private owners only — offices go through OFFICE proceedings
      .map((o) => {
        // SECURED only blocks when it's on the CURRENT route version; a stale SECURED title
        // (route changed) still needs re-acquisition, so the owner stays selectable.
        const secured = o.access?.status === "secured" && !o.access?.is_stale;
        const inField = o.in_field; // an order already in progress — informational, not blocking
        return { owner: o, secured, inField, blocked: secured };
      });
    if (rows.length) groups.push({ parcel: p, rows });
  }
  return groups;
}

export function WpwRequisitionModal({
  connectionId,
  projectId,
  parcels,
  onClose,
  onCreated,
  inline = false,
}: {
  connectionId: string;
  // hub project id — the ":id" slot of the wpw-masowe route holds the PROJECT id, not the connection
  projectId?: string;
  parcels: Parcel[];
  onClose: () => void;
  // wywołane z id utworzonego zlecenia — kreator finalizuje wtedy szkic sekwencji
  onCreated?: (orderId: string) => void;
  // true = formularz osadzony w zakładce WPW MASOWE (bez overlaya/szuflady)
  inline?: boolean;
}) {
  const mutation = useCreateWpwRequisition(connectionId);
  // Tylko firmy zajmujące się zbieraniem WPW: typ prac „Negocjator WPW" albo „Geodeta z WPW"
  // w kartotece ALBO aktywna pozycja WPW w cenniku umowy ramowej (np. firmy geodezyjne z
  // „Pozyskanie 1 zgody WPW"). Krąg liczy backend (?eligible_for=wpw) — ten sam, co
  // podpowiedź i „Planowane do zlecenia".
  const { data: contractors, isLoading: contractorsLoading } = useActiveContractors(true, "wpw");
  // Integracja z WPW MASOWE: skompilowany pakiet działki = automatyczny komplet
  // wejściowy właściciela (ręczny upload tylko nadpisuje).
  const { data: wpwPackages, isPending: packagesPending } = useWpwPackages(connectionId);
  // Pakiet WPW jest per WŁAŚCICIEL → lookup po owner_id (nie po działce).
  const packagesByOwner = useMemo(() => {
    const m = new Map<string, WpwPackageRow>();
    for (const p of wpwPackages ?? []) if (p.owner_id) m.set(p.owner_id, p);
    return m;
  }, [wpwPackages]);
  // Mandatory GK→podwykonawca input documents, per WPW (owner). Każdy zaznaczony właściciel
  // musi mieć ≥1 plik — pokażą się one podwykonawcy na zleceniu.
  const [ownerFiles, setOwnerFiles] = useState<Record<string, File[]>>({});
  // Lista załączników per właściciel jest ROZWIJANA: przy kilkudziesięciu właścicielach
  // zajmowała pół ekranu. null = domyślnie: rozwinięta tylko, gdy komuś brakuje kompletu.
  const [attachmentsOpen, setAttachmentsOpen] = useState<boolean | null>(null);
  const [isFree, setIsFree] = useState(false);
  const [approveNow, setApproveNow] = useState(false);
  // Bramka approve_now (lustro backendu): dyrektor / CEO. Bez tego prawa checkbox
  // się nie renderuje i approveNow zostaje false.
  const currentUser = useAuthStore((st) => st.user);
  const canApproveNow = !!currentUser && (currentUser.is_ceo || currentUser.role === "director");
  const navigate = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  // Potwierdzenie sukcesu — brak systemu toastów, więc komunikat inline (jak `err`).
  // Widoczne w osadzeniach inline (zakładka WPW MASOWE / kreator sekwencji); w szufladzie
  // onClose zamyka formularz, więc samo zniknięcie jest potwierdzeniem.
  const [done, setDone] = useState<string | null>(null);
  // true = powstało ZAPOTRZEBOWANIE (wymaga akceptacji) — pokazujemy czerwony banner;
  // false = approve_now, zamówienie istnieje od razu i banner nie ma sensu.
  const [doneNeedsApproval, setDoneNeedsApproval] = useState(false);

  const groups = useMemo(() => buildGroups(parcels), [parcels]);
  const allRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  const selectableCount = useMemo(() => allRows.filter((r) => !r.blocked).length, [allRows]);

  // Owners clean to send: not secured, not already in the field. In-field owners stay selectable
  // but UNCHECKED by default, so a second order is a deliberate choice.
  const cleanRows = useMemo(() => allRows.filter((r) => !r.secured && !r.inField), [allRows]);
  // Preselekcja = TYLKO właściciele ze SKOMPILOWANYM pakietem z poprzedniego kroku
  // („Generuj i kompiluj"). Wcześniej zaznaczali się wszyscy właściciele trasy, więc przy
  // działce z kilkudziesięcioma współwłaścicielami trzeba było ręcznie odznaczyć resztę.
  // Gdy w projekcie nie ma ani jednego skompilowanego pakietu (obieg z ręcznym wgraniem
  // kompletów), zostaje stare zachowanie: wszyscy „czyści" właściciele.
  const compiledRows = useMemo(
    () => cleanRows.filter((r) => isWpwPackageUsable(packagesByOwner.get(r.owner.id))),
    [cleanRows, packagesByOwner],
  );
  const defaultRows = useMemo(
    () => (compiledRows.length ? compiledRows : cleanRows),
    [compiledRows, cleanRows],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Seed leci RAZ, dopiero gdy DOJADĄ działki i pakiety (obie listy są asynchroniczne) —
  // liczenie preselekcji przy montowaniu trafiało na pustą listę i nie zaznaczało nikogo.
  // Błąd pobrania pakietów (isPending schodzi na false z pustym `data`) też odpala seed —
  // wtedy po prostu działa gałąź „wszyscy czyści", zamiast zostawić formularz bez zaznaczeń.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || packagesPending || allRows.length === 0) return;
    seededRef.current = true;
    setSelected(new Set(defaultRows.map((r) => r.owner.id)));
  }, [allRows, defaultRows, packagesPending]);
  const ownerCount = selected.size;
  const selectedRows = useMemo(
    () =>
      groups.flatMap((g) =>
        g.rows.filter((r) => selected.has(r.owner.id))
          .map((r) => ({ ...r, parcelTeryt: g.parcel.teryt, parcelId: g.parcel.id })),
      ),
    [groups, selected],
  );

  const { register, control, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      wpw_order_type: "wpw_kompleksowe",
      description: "",
      line_items: [
        { is_custom: false, line_item_type: "", quantity: 1, description: "", attachment_required: false, result_files_expected: false, required_file_extension: "" },
      ],
    },
  });
  const { fields, append, remove, insert } = useFieldArray({ control, name: "line_items" });
  const contractorId = useWatch({ control, name: "contractor" });
  // Termin realizacji do podpowiedzi: firma z urlopem w trakcie zlecenia dostaje dopisek.
  const watchedDeadline = useWatch({ control, name: "deadline" });
  const { data: agreements } = useContractorAgreements(contractorId || undefined);
  // Pozycje automatyczne umowy: po wyborze pozycji wyzwalającej wiersz automatyczny dopisuje
  // się pod nią sam (jak „+ Z umowy", ale z informacjami z tabeli umowy ramowej). Backend i
  // tak by je dodał przy tworzeniu zamówienia — tu użytkownik widzi je przed wysłaniem.
  const { data: autoPreview } = useAutoLineItemsPreview(contractorId || undefined, connectionId);
  const autoById = useMemo(
    () => Object.fromEntries((autoPreview ?? []).map((a) => [a.id, a])),
    [autoPreview],
  );
  const autoByTrigger = useMemo(() => {
    const m: Record<string, NonNullable<typeof autoPreview>> = {};
    // Pozycja może mieć kilka wyzwalaczy — figuruje pod każdym z nich; wiersz i tak
    // wchodzi raz (pod pierwszym obecnym wyzwalaczem), patrz presentAuto niżej.
    for (const a of autoPreview ?? []) {
      for (const t of a.auto_triggers ?? []) (m[t] ??= []).push(a);
    }
    return m;
  }, [autoPreview]);
  const watchedItems = useWatch({ control, name: "line_items" }) as FormData["line_items"] | undefined;
  // Pozycje automatyczne odznaczone przez użytkownika — nie wracają pod wyzwalacz, a backend
  // dostaje je jako declined_auto_line_item_types (automat nie dopisze ich do zamówienia).
  const [declinedAuto, setDeclinedAuto] = useState<Set<string>>(new Set());
  useEffect(() => setDeclinedAuto(new Set()), [contractorId]);

  useEffect(() => {
    const items = watchedItems ?? [];
    const triggerTypes = new Set(
      items.filter((r) => !r.auto_trigger && r.line_item_type && !autoById[r.line_item_type]).map((r) => r.line_item_type!),
    );
    // 1. Wiersz automatyczny bez swojej pozycji wyzwalającej (usunięta / zmieniona) znika.
    for (let i = items.length - 1; i >= 0; i--) {
      const r = items[i];
      if (!r.auto_trigger) continue;
      const valid =
        triggerTypes.has(r.auto_trigger) &&
        (autoByTrigger[r.auto_trigger] ?? []).some((a) => a.applicable && a.id === r.line_item_type);
      if (!valid) {
        remove(i);
        return; // jedna zmiana na przebieg — efekt odpali się ponownie po aktualizacji
      }
    }
    // 2. Brakujący wiersz automatyczny wchodzi tuż pod swoją pozycją wyzwalającą — chyba że
    //    użytkownik wskazał tę pozycję ręcznie z listy „Z umowy" (wtedy nie dublujemy).
    const presentAuto = new Set(items.filter((r) => r.auto_trigger || autoById[r.line_item_type ?? ""]).map((r) => r.line_item_type));
    for (let i = 0; i < items.length; i++) {
      const r = items[i];
      if (r.auto_trigger || !r.line_item_type || autoById[r.line_item_type]) continue;
      for (const a of autoByTrigger[r.line_item_type] ?? []) {
        if (!a.applicable || presentAuto.has(a.id) || declinedAuto.has(a.id)) continue;
        insert(i + 1, {
          is_custom: false,
          line_item_type: a.id,
          auto_trigger: r.line_item_type,
          quantity: autoRowQuantity(a, r.quantity),
          description: "",
          attachment_required: a.attachment_required,
          result_files_expected: false,
          required_file_extension: "",
        });
        return;
      }
    }
    // 3. „Ilość jak w pozycji wyzwalającej" podąża za ilością wyzwalacza.
    for (let i = 0; i < items.length; i++) {
      const r = items[i];
      if (!r.auto_trigger || !r.line_item_type) continue;
      const a = autoById[r.line_item_type];
      if (!a) continue;
      const trigger = items.find((t) => !t.auto_trigger && t.line_item_type === r.auto_trigger);
      const want = autoRowQuantity(a, trigger?.quantity);
      if (Number(r.quantity) !== want) setValue(`line_items.${i}.quantity`, want);
    }
  }, [watchedItems, autoByTrigger, autoById, declinedAuto, insert, remove, setValue]);

  const lineItemOptions = useMemo(
    () =>
      (agreements ?? []).flatMap((a) =>
        a.line_item_types
          .filter((lit) => lit.is_active !== false)
          .map((lit) => ({
            value: lit.id,
            // Pozycja automatyczna dopisuje się sama przy wyzwalaczu, ale można ją też wskazać
            // ręcznie — wtedy wiersz wygląda tak samo jak dopisany automatycznie.
            label: lit.is_auto
              ? `⚡ ${lit.name} — pozycja automatyczna (dodawana przy ${autoTriggerLabel(lit)})`
              : `${lit.name} — ${lit.unit}${lit.base_rate != null ? ` @ ${parseFloat(lit.base_rate).toLocaleString("pl-PL", { useGrouping: "always" })} zł` : ""}`,
          })),
      ),
    [agreements],
  );
  const lineItemAttachmentMap = useMemo<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        (agreements ?? []).flatMap((a) => a.line_item_types.map((lit) => [lit.id, lit.attachment_required])),
      ),
    [agreements],
  );

  // Changing contractor invalidates previously picked framework positions.
  useEffect(() => {
    fields.forEach((_, i) => setValue(`line_items.${i}.line_item_type`, ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorId]);

  // Prefill the (single) line-item quantity with the selected-owner count.
  useEffect(() => {
    if (fields.length === 1) setValue("line_items.0.quantity", ownerCount || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerCount]);

  // Firma na urlopie w oknie składania zamówienia idzie na dół pickera z dopiskiem
  // „(urlop od–do)" — nadal do wyboru.
  const contractorOptions = useMemo(
    () =>
      sortLeaveLast(
        (contractors ?? []).map((c) => ({
          value: c.id,
          label: withLeaveSuffix(c.short_name ? `${c.name} (${c.short_name})` : c.name, c.on_leave),
          onLeave: !!c.on_leave,
        })),
      ),
    [contractors],
  );
  // Pusta lista = w bazie nie ma firmy ani z typem prac WPW, ani z pozycją WPW
  // w cenniku — bez podpowiedzi user widziałby pusty select i nie wiedziałby, co ustawić.
  const noNegotiators = !contractorsLoading && contractorOptions.length === 0;
  // Krąg podpowiedzi = dokładnie firmy z pickera (krąg WPW).
  const negotiatorIds = useMemo(() => contractorOptions.map((o) => o.value), [contractorOptions]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const onSubmit = (data: FormData) => {
    setErr(null);
    setDone(null);
    if (selected.size === 0) {
      setErr("Zaznacz co najmniej jednego właściciela (WPW).");
      return;
    }
    if (approveNow && !data.deadline) {
      // Backend odrzuca zatwierdzenie zlecenia bez terminu realizacji (orders.services) —
      // mówimy to przed wysłaniem, zamiast pokazywać 400 przy etykiecie „opcjonalny".
      setErr("Przy natychmiastowym zatwierdzeniu w zlecenie podaj termin realizacji.");
      return;
    }
    // Komplet ręczny ALBO SKOMPILOWANY pakiet WPW MASOWE działki właściciela.
    const missing = selectedRows.filter(
      (r) => (ownerFiles[r.owner.id]?.length ?? 0) === 0 && !isWpwPackageUsable(packagesByOwner.get(r.owner.id)),
    );
    if (missing.length) {
      setAttachmentsOpen(true);
      setErr(
        "Brak kompletu wejściowego (wgraj pliki albo wygeneruj pakiet w WPW MASOWE) dla: "
        + missing.map((r) => r.owner.full_name || r.parcelTeryt).join(", ") + ".",
      );
      return;
    }
    const fd = new FormData();
    fd.append("contractor", data.contractor);
    fd.append("priority", "standard");
    fd.append("wpw_order_type", data.wpw_order_type);
    fd.append("is_free", isFree ? "true" : "false");
    fd.append("approve_now", approveNow ? "true" : "false");
    if (data.description) fd.append("description", data.description);
    if (data.deadline) fd.append("deadline", data.deadline);
    const line_items = data.line_items.map((it) => ({
      line_item_type: it.line_item_type,
      quantity: it.quantity,
      description: it.description,
      attachment_required: it.attachment_required,
      required_file_extension: it.required_file_extension,
    }));
    fd.append("line_items", JSON.stringify(line_items));
    declinedAuto.forEach((id) => fd.append("declined_auto_line_item_types", id));
    [...selected].forEach((id) => {
      fd.append("selected_owner_ids", id);
      (ownerFiles[id] ?? []).forEach((f) => fd.append(`files_${id}`, f));
    });
    mutation.mutate(fd, {
      onSuccess: (resp) => {
        // approve_now: the WPW order exists — jump straight into the mass-package form.
        const orderId = (resp as { order_id?: string } | undefined)?.order_id;
        setDone(
          approveNow
            ? `Zlecenie WPW utworzone dla ${ownerCount} właścicieli.`
            : `Zapotrzebowanie WPW utworzone dla ${ownerCount} właścicieli.`,
        );
        setDoneNeedsApproval(!approveNow);
        if (orderId) onCreated?.(orderId);
        onClose();
        if (approveNow && orderId && projectId) {
          navigate(`/power-projects/${projectId}/wpw-masowe/${orderId}`);
        }
      },
      onError: (e: unknown) =>
        setErr(
          (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data?.errors?.detail ||
            "Nie udało się utworzyć zapotrzebowania.",
        ),
    });
  };

  const formBody = (
        <form onSubmit={handleSubmit(onSubmit)}
              className={inline ? "space-y-5" : "flex-1 overflow-y-auto px-5 py-4 space-y-5"}>
          {/* ── WPW per owner — pick which owners to send the negotiator for ── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <span className="section-label">WPW — właściciele prywatni ({selected.size}/{selectableCount})</span>
              <span className="text-[11px] font-mono text-[var(--color-text-muted)]">{ownerCount} do wysłania</span>
            </div>
            {/* Skróty zaznaczania — przy działkach z kilkudziesięcioma współwłaścicielami
                klikanie po jednym polu jest nierealne. */}
            {allRows.length > 0 && (
              <div className="flex items-center gap-2 mb-2 text-[10px] font-mono text-[var(--color-text-muted)]">
                <span>zaznacz:</span>
                <button
                  type="button"
                  className="underline hover:text-[var(--color-primary)] disabled:opacity-40 disabled:no-underline"
                  title="Tylko właściciele, dla których pakiet WPW został wygenerowany i skompilowany"
                  disabled={compiledRows.length === 0}
                  onClick={() => setSelected(new Set(compiledRows.map((r) => r.owner.id)))}
                >
                  ze sprawdzonym pakietem ({compiledRows.length})
                </button>
                <span>·</span>
                <button
                  type="button"
                  className="underline hover:text-[var(--color-primary)]"
                  title="Wszyscy poza pozyskanymi i tymi z zamówieniem WPW w trakcie (te zaznaczasz ręcznie)"
                  onClick={() => setSelected(new Set(cleanRows.map((r) => r.owner.id)))}
                >
                  wszystkich ({cleanRows.length})
                </button>
                <span>·</span>
                <button
                  type="button"
                  className="underline hover:text-[var(--color-primary)]"
                  onClick={() => setSelected(new Set())}
                >
                  nikogo
                </button>
              </div>
            )}
            {allRows.length === 0 ? (
              <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
                Brak właścicieli prywatnych na działkach.
              </p>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-[var(--color-border)] divide-y divide-[var(--color-border-muted)]">
                {groups.map((g) => {
                  // Małżeństwa jak w widoku działek (groupOwnersForDisplay): małżonkowie
                  // obok siebie ze wspólnym udziałem i dopiskiem „małżeństwo", grupy
                  // posortowane od największego udziału.
                  const rowById = new Map(g.rows.map((row) => [row.owner.id, row] as const));
                  const displayGroups = groupOwnersForDisplay(g.rows.map((row) => row.owner));
                  return (
                  <div key={g.parcel.id}>
                    <div className="px-3 py-1 text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)]">
                      {g.parcel.teryt}
                    </div>
                    {displayGroups.map((og) => og.owners.map((o) => {
                      const r = rowById.get(o.id)!;
                      return (
                      <label
                        key={r.owner.id}
                        title={r.secured ? "Zgoda już pozyskana" : r.inField ? "Zamówienie WPW już w trakcie — można wysłać kolejne" : undefined}
                        className={`flex items-center gap-2 px-3 py-1.5 text-[11px] font-mono ${
                          r.blocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-[var(--color-bg-elevated)]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(r.owner.id)}
                          disabled={r.blocked}
                          onChange={() => toggle(r.owner.id)}
                        />
                        <span className="text-[var(--color-text-primary)] truncate">{r.owner.full_name || "—"}</span>
                        {o.ownership_share && (
                          <span
                            className={`shrink-0 whitespace-nowrap ${
                              og.married
                                ? "text-rose-700 dark:text-rose-400 font-semibold"
                                : "text-[var(--color-text-muted)]"
                            }`}
                            title={og.married
                              ? "Wspólność małżeńska według wypisu — wspólny udział małżonków, podpisują oboje"
                              : "Udział według wypisu"}
                          >
                            ({o.ownership_share})
                          </span>
                        )}
                        {o.own_share && (
                          <span
                            className="shrink-0 whitespace-nowrap text-[var(--color-text-muted)]"
                            title="Udział trzymany samodzielnie, obok udziału we wspólności małżeńskiej — jeden pakiet z oboma udziałami"
                          >
                            ({o.own_share} samodzielnie)
                          </span>
                        )}
                        {og.married && (
                          <span
                            className="shrink-0 px-1 py-px rounded text-[8px] font-semibold uppercase tracking-wide bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                            title="Wspólność małżeńska według wypisu — jeden wspólny udział, jeden podpis obojga"
                          >
                            małżeństwo
                          </span>
                        )}
                        <span className="ml-auto flex shrink-0 items-center gap-1">
                          {/* Skompilowany pakiet z kroku „Generuj i kompiluj" — to on decyduje
                              o preselekcji, więc musi być widać, kogo i dlaczego zaznaczono. */}
                          {isWpwPackageUsable(packagesByOwner.get(r.owner.id)) && (
                            <span className="px-1 py-px rounded text-[9px] font-semibold bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">
                              PAKIET
                            </span>
                          )}
                          {r.secured && (
                            <span className="px-1 py-px rounded text-[9px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              POZYSKANY
                            </span>
                          )}
                          {!r.secured && r.inField && (
                            <span className="px-1 py-px rounded text-[9px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                              W TRAKCIE
                            </span>
                          )}
                        </span>
                      </label>
                      );
                    }))}
                  </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── contractor + WPW type ── */}
          {/* Controller (not register): SearchableSelect only reflects values passed via the
              value prop — with register, a pick from the suggestion panel (setValue) landed in
              the form state but the picker kept showing the placeholder. */}
          <Controller
            name="contractor"
            control={control}
            render={({ field }) => (
              <SearchableSelect
                label="Podwykonawca (WPW)"
                placeholder={
                  contractorsLoading
                    ? "Ładowanie…"
                    : noNegotiators
                      ? "— brak firm do zlecenia WPW —"
                      : "— wybierz podwykonawcę —"
                }
                options={contractorOptions}
                error={errors.contractor?.message}
                disabled={contractorsLoading || noNegotiators}
                name={field.name}
                value={field.value ?? ""}
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
          {noNegotiators && (
            <p className="-mt-2 text-xs text-amber-700">
              Żadna aktywna firma nie ma typu prac „Negocjator WPW” / „Geodeta z WPW” ani
              aktywnej pozycji WPW w cenniku umowy ramowej. Zaznacz typ w karcie podwykonawcy
              (Podwykonawcy → edycja firmy → Typy prowadzonych prac) albo dodaj pozycję WPW
              do umowy ramowej firmy.
            </p>
          )}
          {/* Podpowiedź negocjatora: historia zleceń na TYM projekcie (np. MDCP sprzed kilku
              godzin) + „Zasady podpowiadania" + AI. Propozycja — klik tylko ustawia picker. */}
          {!noNegotiators && (
            <ContractorSuggestionPanel
              scope="wpw"
              connectionId={connectionId}
              candidates={negotiatorIds}
              selectedId={contractorId || undefined}
              onPick={(id) => setValue("contractor", id, { shouldValidate: true, shouldDirty: true })}
              enabled={!contractorsLoading && negotiatorIds.length > 0}
              deadline={watchedDeadline || undefined}
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="section-label">Typ zlecenia WPW</span>
              <select className="input-base mt-1 text-xs py-1.5" {...register("wpw_order_type")}>
                <option value="wpw_kompleksowe">WPW kompleksowe (art.124)</option>
                <option value="wpw_terenowe">WPW terenowe</option>
              </select>
            </label>
            <label className="block">
              <span className="section-label">
                Termin {approveNow ? "(wymagany przy natychmiastowym zatwierdzeniu)" : "(opcjonalny)"}
              </span>
              <Controller
                name="deadline"
                control={control}
                render={({ field }) => (
                  <DatePicker className="mt-1 py-1.5" value={field.value ?? ""} onChange={field.onChange} />
                )}
              />
            </label>
          </div>
          <label className="block">
            <span className="section-label">Opis (opcjonalny)</span>
            <textarea
              className="input-base mt-1 text-xs"
              rows={2}
              placeholder="Domyślnie: „WPW — pozyskanie zgód prywatnych”"
              {...register("description")}
            />
          </label>
          <label className="flex items-start gap-2 text-[11px] font-mono text-[var(--color-text-secondary)]">
            <input type="checkbox" className="mt-0.5" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />
            <span>
              Zlecenie darmowe (poprawka wykonawcy po zmianie trasy).
              <span className="text-[var(--color-text-muted)]"> Dla zlecenia bezpłatnego użyj pozycji ze stawką 0.</span>
            </span>
          </label>
          {/* Zatwierdzanie wydaje pieniądze — od 25.09.2026 to decyzja dyrektora/CEO
              (koordynator przedsięwzięcia już nie wystarcza): innym checkbox się nie
              pokazuje, ich zapotrzebowanie zawsze ląduje jako „Złożone" do akceptacji. */}
          {canApproveNow && (
            <label className="flex items-start gap-2 text-[11px] font-mono text-[var(--color-text-secondary)]">
              <input
                type="checkbox" className="mt-0.5" checked={approveNow}
                onChange={(e) => setApproveNow(e.target.checked)}
              />
              <span>
                Zatwierdź od razu w zlecenie (dyrektor / CEO).
                <span className="text-[var(--color-text-muted)]">
                  {" "}Zamówienie WPW powstaje natychmiast i otwiera się formularz masowej
                  generacji pakietów ENERGA.
                </span>
              </span>
            </label>
          )}

          {/* ── line items ── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <span className="section-label">Pozycje realizacyjne</span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() =>
                  append({ is_custom: false, line_item_type: "", quantity: ownerCount || 1, description: "", attachment_required: false, result_files_expected: false, required_file_extension: "" })
                }
              >
                <Plus size={14} /> Z umowy
              </Button>
            </div>
            {errors.line_items?.root && <p className="text-xs text-red-600 mb-2">{errors.line_items.root.message}</p>}
            {!contractorId && (
              <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 mb-2">
                Wybierz podwykonawcę, aby wskazać pozycje z umowy ramowej.
              </p>
            )}
            <div className="flex flex-col gap-3">
              {fields.map((field, index) => {
                const row = watchedItems?.[index];
                const autoItem = autoById[row?.line_item_type ?? ""];
                if (autoItem && row?.auto_trigger) {
                  // Dopisana przy wyzwalaczu — można ją pominąć (np. premia, której zamawiający
                  // nie chce); decyzja idzie z zapotrzebowaniem i automat jej nie przywróci.
                  return (
                    <AutoLineItemRow
                      key={field.id}
                      item={autoItem}
                      quantity={row?.quantity ?? 1}
                      removeTitle="Pomiń pozycję automatyczną — nie zostanie dopisana do zamówienia"
                      onRemove={() => {
                        setDeclinedAuto((s) => new Set(s).add(autoItem.id));
                        remove(index);
                      }}
                    />
                  );
                }
                if (autoItem) {
                  // Wskazana ręcznie z listy „Z umowy": ten sam wiersz, ale do usunięcia i (dla
                  // trybu ilość × stawka) z edytowalną ilością.
                  return (
                    <AutoLineItemRow
                      key={field.id}
                      item={autoItem}
                      quantity={row?.quantity ?? 1}
                      manual
                      onRemove={() => remove(index)}
                      quantityInput={
                        autoItem.auto_pricing_mode === "rate" ? (
                          <input
                            type="number"
                            step="any"
                            min="0"
                            className="input-base w-24 py-1 text-xs"
                            {...register(`line_items.${index}.quantity`)}
                          />
                        ) : undefined
                      }
                    />
                  );
                }
                const skipped = row?.line_item_type
                  ? (autoByTrigger[row.line_item_type] ?? []).filter((a) => !a.applicable)
                  : [];
                // Odznaczone przez użytkownika — pokazujemy pod pierwszym wyzwalaczem z „Przywróć".
                const declinedHere = row?.line_item_type
                  ? (autoByTrigger[row.line_item_type] ?? []).filter(
                      (a) =>
                        a.applicable &&
                        declinedAuto.has(a.id) &&
                        watchedItems?.findIndex(
                          (t) => !t.auto_trigger && !!t.line_item_type && a.auto_triggers.includes(t.line_item_type),
                        ) === index,
                    )
                  : [];
                return (
                  <div key={field.id} className="flex flex-col gap-1.5">
                    <LineItemFormRow
                      index={index}
                      control={control}
                      register={register}
                      errors={errors}
                      lineItemOptions={lineItemOptions}
                      contractorId={contractorId || undefined}
                      onRemove={() => remove(index)}
                      lineItemAttachmentMap={lineItemAttachmentMap}
                      setValue={setValue}
                    />
                    {skipped.map((a) => (
                      <p key={a.id} className="ml-6 text-[11px] text-[var(--color-text-muted)]">
                        Pozycja automatyczna „{a.name}” nie zostanie dodana: {a.skip_reason}
                      </p>
                    ))}
                    {declinedHere.map((a) => (
                      <p key={a.id} className="ml-6 text-[11px] text-[var(--color-text-muted)]" data-testid="declined-auto-line-item">
                        Pominięto pozycję automatyczną „{a.name}” — nie zostanie dopisana do zamówienia.{" "}
                        <button
                          type="button"
                          className="text-[var(--color-primary)] hover:underline"
                          onClick={() =>
                            setDeclinedAuto((s) => {
                              const next = new Set(s);
                              next.delete(a.id);
                              return next;
                            })
                          }
                        >
                          Przywróć
                        </button>
                      </p>
                    ))}
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── per-owner mandatory attachments (GK → podwykonawca) — rozwijana lista ── */}
          <section>
            {(() => {
              const missingCount = selectedRows.filter(
                (r) => (ownerFiles[r.owner.id]?.length ?? 0) === 0 && !isWpwPackageUsable(packagesByOwner.get(r.owner.id)),
              ).length;
              const open = attachmentsOpen ?? missingCount > 0;
              return (
                <>
                  <button
                    type="button"
                    className="flex w-full items-center gap-1.5 mb-1 text-left"
                    onClick={() => setAttachmentsOpen(!open)}
                    aria-expanded={open}
                  >
                    {open ? (
                      <ChevronDown size={14} className="text-[var(--color-text-muted)]" />
                    ) : (
                      <ChevronRight size={14} className="text-[var(--color-text-muted)]" />
                    )}
                    <FileText size={12} className="text-[var(--color-text-muted)]" />
                    <span className="section-label">Załączniki per WPW (właściciel) — wymagane</span>
                    <span className="ml-auto text-[11px] font-mono text-[var(--color-text-muted)]">
                      {selectedRows.length === 0
                        ? "nikt nie zaznaczony"
                        : missingCount > 0
                          ? <span className="text-amber-700">{selectedRows.length} właśc. · {missingCount} bez kompletu</span>
                          : <span className="text-green-700">{selectedRows.length} właśc. · komplet</span>}
                    </span>
                  </button>
                  {!open && <div className="mb-2" />}
                  {open && (
            <>
            <p className="text-[10px] text-[var(--color-text-muted)] mb-2">
              Dokumenty wejściowe dla podwykonawcy — po jednym komplecie na każdego zaznaczonego właściciela.
            </p>
            {selectedRows.length === 0 ? (
              <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
                Zaznacz właścicieli powyżej, aby dołączyć dokumenty.
              </p>
            ) : (
              <div className="space-y-2">
                {selectedRows.map((r) => {
                  const has = (ownerFiles[r.owner.id]?.length ?? 0) > 0;
                  const pkg = packagesByOwner.get(r.owner.id);
                  const covered = has || isWpwPackageUsable(pkg);
                  return (
                    <div key={r.owner.id} className={`border p-2 space-y-1 ${covered ? "border-[var(--color-border)]" : "border-amber-300 bg-amber-50"}`}>
                      <div className="text-[11px] font-mono text-[var(--color-text-secondary)]">
                        {r.parcelTeryt} · {r.owner.full_name || "—"}
                        {!covered && !pkg && (
                          <span className="ml-1 text-amber-700">
                            — brak kompletu: wgraj pliki albo wypełnij pakiet działki powyżej
                          </span>
                        )}
                        {!covered && pkg && (
                          <span className="ml-1 text-amber-700">
                            — {pkg.usable?.reason || `Pakiet ${pkg.number} nie jest skompilowany.`}
                          </span>
                        )}
                      </div>
                      {pkg && isWpwPackageUsable(pkg) && !has && (
                        // Integracja z WPW MASOWE — komplet wchodzi automatycznie z pakietu działki.
                        <div className="text-[11px] text-green-700">
                          ✓ automatycznie z WPW MASOWE: {pkg.number} · {pkg.file_name}
                          <span className="text-[var(--color-text-muted)]"> (upload poniżej nadpisuje)</span>
                        </div>
                      )}
                      <MultiFileInput
                        files={ownerFiles[r.owner.id] ?? []}
                        onChange={(fs) => setOwnerFiles((m) => ({ ...m, [r.owner.id]: fs }))}
                        label="Dodaj pliki"
                      />
                    </div>
                  );
                })}
              </div>
            )}
            </>
                  )}
                </>
              );
            })()}
          </section>

          {err && <p className="text-[11px] text-red-600">{err}</p>}
          {done && (
            <>
              <p className="text-[11px] text-green-700 bg-green-50 border border-green-200 px-3 py-2 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/40">
                ✓ {done}
              </p>
              {doneNeedsApproval && <RequisitionApprovalNotice withLink />}
            </>
          )}

          <div className="flex gap-2 pt-1">
            <Button type="submit" loading={mutation.isPending} disabled={selected.size === 0}>
              Utwórz zapotrzebowanie
            </Button>
            {!inline && (
              <Button type="button" variant="ghost" onClick={onClose}>Anuluj</Button>
            )}
          </div>
        </form>
  );

  if (inline) return formBody;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/30 backdrop-blur-[2px] animate-overlay-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="animate-drawer-in flex h-full w-full max-w-xl flex-col bg-[var(--color-bg-surface)] border-l border-[var(--color-border-strong)] shadow-[-8px_0_28px_rgba(0,0,0,0.14)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shrink-0 flex items-start justify-between gap-3 px-5 py-4 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border-strong)]">
          <div className="flex items-center gap-2">
            <HardHat size={16} className="text-[var(--color-primary)]" />
            <h3 className="font-mono text-sm font-bold uppercase tracking-widest text-[var(--color-text-primary)]">
              Zapotrzebowanie — zgody prywatne (WPW)
            </h3>
          </div>
          <button onClick={onClose} title="Zamknij" className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]">
            <X size={18} />
          </button>
        </header>
        {formBody}
      </div>
    </div>
  );
}
